/**
 * Operation Parameter Types
 *
 * Based on LogicAppsUX patterns for defining and managing operation parameters.
 * Parameters define the inputs that users configure for each action/trigger.
 */

import type { ValueSegment, SchemaObject } from './valueSegment';

// =============================================================================
// PARAMETER INFO
// =============================================================================

/**
 * Complete parameter information for UI rendering and value management
 */
export interface ParameterInfo {
  /** Alternative key for parameter lookup */
  alternativeKey?: string;
  /** Whether visibility depends on other parameters */
  conditionalVisibility?: boolean;
  /** Dynamic data loading state */
  dynamicData?: DynamicDataState;
  /** Editor type (e.g., 'combobox', 'code', 'condition') */
  editor?: ParameterEditor;
  /** Editor-specific options */
  editorOptions?: Record<string, unknown>;
  /** Editor view model for complex editors */
  editorViewModel?: unknown;
  /** Detailed parameter information */
  info: ParameterDetails;
  /** Whether to hide in UI */
  hideInUI?: boolean;
  /** Unique parameter ID */
  id: string;
  /** Display label */
  label: string;
  /** Key used in the parameter map */
  parameterKey: string;
  /** Parameter name in the operation definition */
  parameterName: string;
  /** Regex pattern for validation */
  pattern?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Default value from swagger/manifest (used to pre-populate the field) */
  defaultValue?: unknown;
  /** Preserved value (before dynamic schema updates) */
  preservedValue?: unknown;
  /** Whether the parameter is required */
  required: boolean;
  /** JSON Schema for the parameter */
  schema?: SchemaObject;
  /** Whether to show validation errors */
  showErrors?: boolean;
  /** Whether to show token picker */
  showTokens?: boolean;
  /** Suppress automatic type casting */
  suppressCasting?: boolean;
  /** Parameter data type */
  type: ParameterType;
  /** Current validation errors */
  validationErrors?: string[];
  /** Current value as value segments */
  value: ValueSegment[];
  /** Visibility level */
  visibility?: ParameterVisibility;
}

// =============================================================================
// PARAMETER DETAILS
// =============================================================================

/**
 * Detailed metadata about a parameter
 */
export interface ParameterDetails {
  /** Alias name for the parameter */
  alias?: string;
  /** Key for associated array item parameter */
  arrayItemInputParameterKey?: string;
  /** Dependencies on other parameters */
  dependencies?: InputDependencies;
  /** Dynamic values configuration from x-ms-dynamic-list or x-ms-dynamic-values */
  dynamicValues?: DynamicValuesConfig;
  /** Dynamic schema configuration from x-ms-dynamic-schema */
  dynamicSchema?: DynamicSchemaConfig;
  /** Dynamic tree configuration from x-ms-dynamic-tree */
  dynamicTree?: DynamicTreeConfig;
  /** Input dependencies for conditional visibility (from x-ms-input-dependencies) */
  inputDependencies?: InputDependency[];
  /** Whether this parameter was generated from a dynamic schema */
  isGeneratedFromSchema?: boolean;
  /** Name of the parameter that triggered the dynamic schema generation */
  generatedByParameter?: string;
  /**
   * For body parameters whose schema is resolved dynamically (x-ms-dynamic-schema),
   * the dotted body sub-path that the resolved children should serialize under.
   * Empty string (or undefined) means the parent IS the body root (e.g. SharePoint
   * PostItem) and children serialize directly under `body`. A non-empty path
   * (e.g. SQL ExecuteProcedure_V2's `parameters`) means children serialize under
   * `body.<path>.<name>`. Mirrors LogicAppsUX's `keyPrefix` (parent's `body.$...`).
   */
  bodyKeyPath?: string;
  /**
   * True when this body parameter's value IS the entire request body
   * (primitive/dictionary schema with no `properties` to flatten). Set by
   * the swagger parser; the serializer writes the value straight to
   * `inputs.body` instead of wrapping it as `{ [paramName]: value }`.
   * Mirrors LogicAppsUX's `body.$` root parameter in `constructInputValues`.
   */
  isBodyRoot?: boolean;
  /** Encoding format (e.g., 'base64') */
  encode?: string;
  /** Value format (e.g., 'date-time', 'email', 'uri') */
  format?: string;
  /** Collection format (e.g., 'csv', 'ssv', 'tsv') */
  collectionFormat?: string;
  /** Parameter location (e.g., 'body', 'query', 'path', 'header') */
  in?: ParameterLocation;
  /** Whether this is a dynamic parameter */
  isDynamic?: boolean;
  /** Reference to the dynamic parameter definition */
  dynamicParameterReference?: string;
  /** Whether managed by a specific editor */
  isEditorManagedItem?: boolean;
  /** Whether this is an unknown/inferred parameter */
  isUnknown?: boolean;
  /**
   * Parent property metadata for nested parameters that have been flattened
   * from a nested object schema. Used by the parameter list to render a
   * collapsible group container around children of the same parent.
   */
  parentProperty?: { key: string; name: string; label: string };
  /** Nested path for parameters flattened from nested objects (e.g., "recipient.groupId") */
  nestedPath?: string;
  /** Serialization options */
  serialization?: ParameterSerializationOptions;
  /** Deserialization options */
  deserialization?: ParameterDeserializationOptions;
  /**
   * For notification triggers, indicates which operation this parameter belongs to.
   * - 'fetch': Parameter is for the fetch operation (retrieves data when notified)
   * - 'subscribe': Parameter is for the subscribe operation (sets up webhook)
   *
   * This is used for proper serialization to inputs.fetch.queries or inputs.subscribe.queries
   */
  notificationLocation?: 'fetch' | 'subscribe';
}

/**
 * Configuration for dynamic dropdown values from x-ms-dynamic-list or x-ms-dynamic-values
 */
export interface DynamicValuesConfig {
  /** Operation ID to call for fetching values */
  operationId: string;
  /** Parameters to pass to the operation (may reference other parameter values) */
  parameters?: Record<string, DynamicValueParameter>;
  /** Path to the array of items in the response (e.g., "value") */
  itemsPath?: string;
  /** Path to the value property in each item (e.g., "id") */
  itemValuePath?: string;
  /** Path to the display name property in each item (e.g., "displayName") */
  itemTitlePath?: string;
  /** Path to the description property in each item */
  itemDescriptionPath?: string;
  /** Whether items can be selected */
  itemSelectablePath?: string;
  /**
   * Whether this is a legacy x-ms-dynamic-values extension (vs modern x-ms-dynamic-list).
   * Legacy extensions use /dynamicInvoke with path-based request body.
   * Modern extensions use /dynamicList with dynamicInvocationDefinition.
   */
  isLegacy?: boolean;
}

/**
 * Parameter reference for dynamic values
 */
export interface DynamicValueParameter {
  /** Reference to another parameter's value (e.g., "poster" for Post as field) */
  parameterReference?: string;
  /** Static value to use */
  value?: unknown;
  /** Whether this parameter is required */
  required?: boolean;
}

/**
 * Configuration for dynamic schema from x-ms-dynamic-schema extension
 *
 * When a parameter has dynamicSchema configured, changing its value triggers
 * a schema fetch that generates additional parameters dynamically.
 */
export interface DynamicSchemaConfig {
  /** Operation ID to call for fetching the schema */
  operationId: string;
  /** Parameters to pass to the operation (may reference other parameter values) */
  parameters?: Record<string, DynamicSchemaParameter>;
  /** Path to the schema object in the response (e.g., "schema") */
  valuePath?: string;
}

/**
 * Parameter reference for dynamic schema
 */
export interface DynamicSchemaParameter {
  /** Reference to another parameter's value */
  parameterReference?: string;
  /** Static value to use */
  value?: unknown;
}

/**
 * Configuration for dynamic tree picker from x-ms-dynamic-tree extension
 *
 * Used for file/folder selection with tree navigation (SharePoint, OneDrive, etc.)
 */
export interface DynamicTreeConfig {
  /** Operation to get root/initial items */
  open: DynamicTreePickerInfo;
  /** Operation to browse/expand folders */
  browse: DynamicTreePickerInfo;
  /** Settings for the tree picker */
  settings?: DynamicTreeSettings;
}

/**
 * Settings for dynamic tree picker
 */
export interface DynamicTreeSettings {
  /** Path to the items array in response (e.g., "value") */
  collectionPath?: string;
  /** Path to value property in each item */
  valuePath?: string;
  /** Path to display name property in each item */
  titlePath?: string;
  /** Property name indicating if item is a folder */
  folderPropertyName?: string;
  /** Property name for media/icon type */
  mediaPropertyName?: string;
  /** Whether parent nodes (folders) can be selected */
  canSelectParentNodes?: boolean;
  /** Whether leaf nodes (files) can be selected */
  canSelectLeafNodes?: boolean;
}

/**
 * Operation info for dynamic tree open/browse
 */
export interface DynamicTreePickerInfo {
  /** Operation ID to call */
  operationId: string;
  /** Parameters to pass to the operation */
  parameters?: Record<string, DynamicTreeParameter>;
  /** Path to items array in response */
  itemsPath?: string;
  /** Path to value property in each item */
  itemValuePath?: string;
  /** Path to display name in each item */
  itemTitlePath?: string;
  /** Path to property indicating if item is a parent/folder */
  itemIsParentPath?: string;
  /** Path to full title/path property */
  itemFullTitlePath?: string;
  /** Filter for selectable items */
  selectableFilter?: string;
}

/**
 * Parameter reference for dynamic tree operations
 */
export interface DynamicTreeParameter {
  /** Reference to another parameter's value */
  parameterReference?: string;
  /** Path to use from selected tree item's value */
  selectedItemValuePath?: string;
  /** Static value to use */
  value?: unknown;
}

// =============================================================================
// PARAMETER TYPES & ENUMS
// =============================================================================

export const ParameterType = {
  String: 'string',
  SecureString: 'securestring',
  Integer: 'integer',
  Number: 'number',
  Boolean: 'boolean',
  Object: 'object',
  Array: 'array',
  Any: 'any',
  File: 'file',
} as const;
export type ParameterType = (typeof ParameterType)[keyof typeof ParameterType];

export const ParameterLocation = {
  Body: 'body',
  Query: 'query',
  Path: 'path',
  Header: 'header',
  FormData: 'formData',
} as const;
export type ParameterLocation = (typeof ParameterLocation)[keyof typeof ParameterLocation];

export const ParameterVisibility = {
  /** Always shown */
  Default: 'default',
  /** Hidden by default, shown in advanced section */
  Advanced: 'advanced',
  /** Hidden from UI */
  Internal: 'internal',
  /** Important - highlighted in UI */
  Important: 'important',
} as const;
export type ParameterVisibility = (typeof ParameterVisibility)[keyof typeof ParameterVisibility];

export const ParameterEditor = {
  /** Standard text input */
  String: 'string',
  /** Multi-line text area */
  TextArea: 'textarea',
  /** Code editor */
  Code: 'code',
  /** Dropdown/combobox with static options */
  Combobox: 'combobox',
  /** Dynamic dropdown that fetches options from connector API */
  DynamicCombobox: 'dynamiccombobox',
  /** Boolean toggle */
  Boolean: 'boolean',
  /** Date picker */
  DateTime: 'datetime',
  /** File picker */
  File: 'file',
  /** Dictionary editor (key-value pairs) */
  Dictionary: 'dictionary',
  /** Array editor */
  Array: 'array',
  /** Condition editor */
  Condition: 'condition',
  /** Authentication editor */
  Authentication: 'authentication',
  /** Schema editor */
  Schema: 'schema',
  /** Table editor */
  Table: 'table',
  /** Recurrence editor */
  Recurrence: 'recurrence',
  /** Copy-paste only */
  CopyPaste: 'copypaste',
  /** Tree-based file/folder picker with navigation */
  FilePicker: 'filepicker',
  /** Rich text HTML editor */
  Html: 'html',
  /** Agent instruction editor (for AI model instructions) */
  AgentInstruction: 'agentinstruction',
  /** Initialize variable editor (for workflow variables) */
  InitializeVariable: 'initializevariable',
  /** Variable name picker (dropdown of defined variables) */
  VariableName: 'variablename',
  /** Workflow picker (dropdown of callable workflows in the same Logic App) */
  WorkflowPicker: 'workflowpicker',
  /** Read-only display of a value owned by an external system (with optional deep-link). */
  ReadOnlyExternal: 'readonlyexternal',
} as const;
export type ParameterEditor = (typeof ParameterEditor)[keyof typeof ParameterEditor];

// =============================================================================
// DYNAMIC DATA
// =============================================================================

export const DynamicLoadStatus = {
  NotStarted: 'notstarted',
  Loading: 'loading',
  Failed: 'failed',
  Succeeded: 'succeeded',
} as const;
export type DynamicLoadStatus = (typeof DynamicLoadStatus)[keyof typeof DynamicLoadStatus];

export interface DynamicDataState {
  /** Loading status */
  status: DynamicLoadStatus;
  /** Error if loading failed */
  error?: DynamicDataError;
}

export interface DynamicDataError {
  /** Error code */
  code?: string;
  /** Error message */
  message: string;
  /** Additional error details */
  details?: unknown;
}

// =============================================================================
// DEPENDENCIES
// =============================================================================

/**
 * Input dependencies for dynamic parameters
 */
export interface InputDependencies {
  /** Parameters this depends on */
  parameters?: ParameterDependency[];
  /** Values this depends on */
  values?: ValueDependency[];
  /** Visibility type (from x-ms-input-dependencies) */
  type?: 'visibility' | 'value';
}

export interface ParameterDependency {
  /** Reference to the dependent parameter */
  parameterReference: string;
  /** Required values for this dependency */
  requiredValues?: unknown[];
}

export interface ValueDependency {
  /** Path to the value */
  path: string;
  /** Required value */
  value: unknown;
}

// =============================================================================
// INPUT DEPENDENCY (x-ms-input-dependencies)
// =============================================================================

/**
 * Dependency type for x-ms-input-dependencies extension
 */
export const InputDependencyType = {
  /** Visibility dependency - show/hide parameter based on other parameter values */
  Visibility: 'visibility',
  /** Value dependency - parameter value depends on other parameter values */
  Value: 'value',
} as const;
export type InputDependencyType = (typeof InputDependencyType)[keyof typeof InputDependencyType];

/**
 * Single input dependency from x-ms-input-dependencies
 *
 * Used to conditionally show/hide parameters based on other parameter values.
 * Multiple dependencies are evaluated with AND logic.
 */
export interface InputDependency {
  /** Type of dependency - visibility or value */
  type: InputDependencyType;
  /** Reference to the parameter this depends on */
  parameterReference: string;
  /** Show when the referenced parameter equals any of these values (OR logic within array) */
  values?: unknown[];
  /** Hide when the referenced parameter equals any of these values (OR logic within array) */
  excludeValues?: unknown[];
}

// =============================================================================
// SERIALIZATION
// =============================================================================

export interface ParameterSerializationOptions {
  /** Property to serialize to */
  property?: SerializationProperty;
  /** Skip serialization */
  skip?: boolean;
  /** Custom serialization format */
  format?: string;
}

export interface ParameterDeserializationOptions {
  /** Property to deserialize from */
  property?: SerializationProperty;
  /** Type to deserialize as */
  type?: string;
}

export interface SerializationProperty {
  /** Property name */
  name: string;
  /** Property type (e.g., 'pathtemplate', 'parentobject', 'formdata') */
  type: string;
  /** Value format */
  format?: string;
  /** Reference to another parameter for value routing (from x-ms-serialization) */
  parameterReference?: string;
}

// =============================================================================
// PARAMETER GROUPS
// =============================================================================

/**
 * A group of related parameters
 */
export interface ParameterGroup {
  /** Unique group ID */
  id: string;
  /** Display name */
  name: string;
  /** Description */
  description?: string;
  /** Parameters in this group */
  parameters: ParameterInfo[];
  /** Whether the group is collapsed by default */
  collapsedByDefault?: boolean;
}

// =============================================================================
// OPERATION MANIFEST PARAMETER DEFINITIONS
// =============================================================================

/**
 * Parameter definition from an operation manifest
 */
export interface OperationParameterDefinition {
  /** Parameter name */
  name: string;
  /** Display name */
  displayName?: string;
  /** Description */
  description?: string;
  /** Data type */
  type: ParameterType;
  /** Whether required */
  required?: boolean;
  /** Default value */
  default?: unknown;
  /** Allowed values for enum types */
  enum?: unknown[];
  /** Value format hint */
  format?: string;
  /** Visibility level */
  visibility?: ParameterVisibility;
  /** Editor type */
  editor?: ParameterEditor;
  /** Editor options */
  editorOptions?: Record<string, unknown>;
  /** JSON Schema */
  schema?: SchemaObject;
  /** Dynamic schema reference */
  dynamicSchema?: DynamicSchemaReference;
}

export interface DynamicSchemaReference {
  /** Operation to call for schema */
  operationId: string;
  /** Parameters to pass to the operation */
  parameters?: Record<string, unknown>;
  /** Path to schema in response */
  valuePath?: string;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a parameter is required
 */
export function isRequiredParameter(param: ParameterInfo): boolean {
  return param.required;
}

/**
 * Check if a parameter is visible (not internal)
 */
export function isVisibleParameter(param: ParameterInfo): boolean {
  return param.visibility !== ParameterVisibility.Internal && !param.hideInUI;
}

/**
 * Check if a parameter is advanced
 */
export function isAdvancedParameter(param: ParameterInfo): boolean {
  return param.visibility === ParameterVisibility.Advanced;
}

/**
 * Check if a parameter has validation errors
 */
export function hasValidationErrors(param: ParameterInfo): boolean {
  return !!param.validationErrors && param.validationErrors.length > 0;
}

/**
 * Get parameters grouped by visibility
 */
export function groupParametersByVisibility(
  params: ParameterInfo[]
): { basic: ParameterInfo[]; advanced: ParameterInfo[] } {
  const visible = params.filter(isVisibleParameter);
  return {
    basic: visible.filter((p) => !isAdvancedParameter(p)),
    advanced: visible.filter((p) => isAdvancedParameter(p)),
  };
}

/**
 * Create default parameter value segments
 */
export function createDefaultParameterValue(
  _type: ParameterType,
  defaultValue?: unknown
): ValueSegment[] {
  const id = `seg_${Date.now()}`;

  if (defaultValue !== undefined) {
    return [
      {
        id,
        type: 'literal',
        value: typeof defaultValue === 'string' ? defaultValue : JSON.stringify(defaultValue),
      },
    ];
  }

  // Return empty value for most types
  return [{ id, type: 'literal', value: '' }];
}

/**
 * Generate a unique parameter ID
 */
export function generateParameterId(parameterName: string, nodeId?: string): string {
  const prefix = nodeId ? `${nodeId}_` : '';
  return `${prefix}${parameterName}_${Date.now()}`;
}
